/**
 * ProjectDetail — project overview with PMI documents and assessment summary.
 */

import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  Users,
  AlertTriangle,
  AlertCircle,
  MessageSquare,
  List,
  FilePlus,
  Clock,
  BookOpen,
  Table2,
  ChevronRight,
  ClipboardList,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { useProjectsWithScores } from '@/hooks/useRCAData';
import { useDocuments, useDocument } from '@/hooks/useRCAData';
import { cn } from '@/lib/utils';
import type { RiskLevel, RCACategory, ProjectDocument } from '@/types/rca';

// ============================================================
// Constants
// ============================================================

const DOC_TYPE_META: Record<string, { label: string; Icon: React.ElementType }> = {
  project_charter:       { label: 'Project Charter',           Icon: FileText },
  stakeholder_register:  { label: 'Stakeholder Register',      Icon: Users },
  risk_register:         { label: 'Risk Register',             Icon: AlertTriangle },
  issue_log:             { label: 'Issue Log',                  Icon: AlertCircle },
  communication_plan:    { label: 'Communication Plan',         Icon: MessageSquare },
  raci_matrix:           { label: 'RACI Matrix',                Icon: Table2 },
  wbs:                   { label: 'Work Breakdown Structure',   Icon: List },
  change_request_form:   { label: 'Change Request Form',        Icon: FilePlus },
  meeting_minutes:       { label: 'Meeting Minutes',            Icon: Clock },
  lessons_learned:       { label: 'Lessons Learned',            Icon: BookOpen },
};

function docMeta(docType: string) {
  return DOC_TYPE_META[docType] ?? { label: docType, Icon: FileText };
}

// ============================================================
// Helpers
// ============================================================

function levelBadgeClass(level: RiskLevel | string | null | undefined): string {
  switch (level) {
    case 'High':   return 'bg-risk-high/10 text-risk-high border-risk-high/30';
    case 'Medium': return 'bg-risk-medium/10 text-risk-medium border-risk-medium/30';
    case 'Low':    return 'bg-risk-low/10 text-risk-low border-risk-low/30';
    default:       return 'bg-muted text-muted-foreground border-border';
  }
}

function categoryBadgeClass(cat: RCACategory | number | null | undefined): string {
  switch (cat) {
    case 4: return 'bg-cat-4/15 text-cat-4 border-cat-4/40';
    case 3: return 'bg-cat-3/15 text-cat-3 border-cat-3/40';
    case 2: return 'bg-cat-2/15 text-cat-2 border-cat-2/40';
    case 1: return 'bg-cat-1/15 text-cat-1 border-cat-1/40';
    default: return 'bg-muted text-muted-foreground border-border';
  }
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'approved': return 'bg-risk-low/10 text-risk-low border-risk-low/30';
    case 'review':   return 'bg-risk-medium/10 text-risk-medium border-risk-medium/30';
    case 'draft':    return 'bg-muted text-muted-foreground border-border';
    default:         return 'bg-muted text-muted-foreground border-border';
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

// ============================================================
// Document Content Viewer
// ============================================================

function renderValue(value: unknown, depth = 0): React.ReactNode {
  if (value === null || value === undefined) return <span className="text-muted-foreground">—</span>;
  if (typeof value === 'boolean') return <span className="text-cot-blue">{value ? 'Yes' : 'No'}</span>;
  if (typeof value === 'number') return <span className="tabular-nums">{value}</span>;
  if (typeof value === 'string') return <span>{value}</span>;

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-muted-foreground italic">Empty</span>;
    return (
      <div className="space-y-2 mt-1">
        {value.map((item, i) => (
          <div key={i} className={cn('border-l-2 border-border pl-3', depth > 0 && 'ml-2')}>
            {renderValue(item, depth + 1)}
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return <span className="text-muted-foreground italic">Empty</span>;
    return (
      <div className={cn('space-y-2', depth > 0 && 'mt-1')}>
        {entries.map(([k, v]) => {
          const label = k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
          const isPrimitive = typeof v !== 'object' || v === null;
          return (
            <div key={k} className={cn(depth > 0 && 'ml-2')}>
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {label}
              </span>
              {isPrimitive ? (
                <div className="text-sm text-foreground mt-0.5">{renderValue(v, depth + 1)}</div>
              ) : (
                <div className="mt-1 pl-3 border-l-2 border-border/60">
                  {renderValue(v, depth + 1)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return <span>{String(value)}</span>;
}

function DocViewerDialog({
  docId,
  onClose,
}: {
  docId: string | null;
  onClose: () => void;
}) {
  const { data: doc, isLoading } = useDocument(docId ?? '');

  return (
    <Dialog open={Boolean(docId)} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          {isLoading ? (
            <Skeleton className="h-5 w-48" />
          ) : doc ? (
            <div className="flex items-center gap-2">
              {(() => {
                const { Icon, label } = docMeta(doc.doc_type);
                return (
                  <>
                    <Icon className="w-4 h-4 text-cot-blue shrink-0" />
                    <span className="font-semibold text-base">{doc.title}</span>
                    <Badge variant="outline" className={cn('text-xs ml-auto', statusBadgeClass(doc.status))}>
                      {doc.status}
                    </Badge>
                    <Badge variant="outline" className="text-xs text-muted-foreground">
                      v{doc.version}
                    </Badge>
                    <span className="sr-only">{label}</span>
                  </>
                );
              })()}
            </div>
          ) : (
            <span>Document not found</span>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 mt-2">
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-4 w-full" />)}
            </div>
          ) : doc?.content ? (
            <div className="text-sm">{renderValue(doc.content)}</div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No content available.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Documents Section
// ============================================================

function DocumentsSection({ projectId }: { projectId: string }) {
  const { data: docs, isLoading, isError } = useDocuments(projectId);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-12 w-full rounded-lg" />)}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
        Failed to load documents.
      </div>
    );
  }

  if (!docs || docs.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-muted/20 px-6 py-8 text-center">
        <ClipboardList className="w-8 h-8 mx-auto mb-2 text-muted-foreground opacity-40" />
        <p className="text-sm text-muted-foreground">No documents on file for this project.</p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableCell className="font-semibold text-muted-foreground text-xs uppercase w-[36px]" />
              <TableCell className="font-semibold text-muted-foreground text-xs uppercase">Document</TableCell>
              <TableCell className="font-semibold text-muted-foreground text-xs uppercase">Status</TableCell>
              <TableCell className="font-semibold text-muted-foreground text-xs uppercase hidden sm:table-cell">Version</TableCell>
              <TableCell className="font-semibold text-muted-foreground text-xs uppercase hidden md:table-cell">Updated</TableCell>
              <TableCell className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.map((doc) => {
              const { label, Icon } = docMeta(doc.doc_type);
              return (
                <TableRow
                  key={doc.id}
                  className="hover:bg-muted/30 cursor-pointer"
                  onClick={() => setSelectedDocId(doc.id)}
                >
                  <TableCell className="pl-4">
                    <Icon className="w-4 h-4 text-cot-blue/70" />
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-sm font-medium text-foreground">{doc.title}</p>
                      <p className="text-xs text-muted-foreground">{label}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn('text-xs capitalize', statusBadgeClass(doc.status))}>
                      {doc.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-xs text-muted-foreground">
                    v{doc.version}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                    {formatDate(doc.updated_at)}
                  </TableCell>
                  <TableCell>
                    <ExternalLink className="w-3.5 h-3.5 text-muted-foreground/60" />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <DocViewerDialog
        docId={selectedDocId}
        onClose={() => setSelectedDocId(null)}
      />
    </>
  );
}

// ============================================================
// Main Page
// ============================================================

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const projectsQuery = useProjectsWithScores();

  const project = projectsQuery.data?.find((p) => p.id === id);

  if (projectsQuery.isLoading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }, (_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
        </div>
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8 text-center">
        <p className="text-muted-foreground">Project not found.</p>
        <Link to="/dashboard" className="mt-4 inline-block text-sm text-cot-blue hover:underline">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  const hasAssessment = Boolean(project.risk_level);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Dashboard
            </button>
            <h1 className="text-2xl font-bold text-foreground">{project.name}</h1>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">{project.department}</span>
              <span className="text-muted-foreground/40 text-xs">•</span>
              <Badge
                variant="outline"
                className={cn(
                  'text-xs capitalize',
                  project.status === 'active'
                    ? 'border-cot-blue/30 text-cot-blue bg-cot-blue/10'
                    : 'text-muted-foreground',
                )}
              >
                {project.status}
              </Badge>
            </div>
          </div>

          <Button
            onClick={() => navigate('/assess')}
            className="shrink-0 bg-cot-blue hover:bg-cot-blue/90 text-white"
          >
            New Assessment
          </Button>
        </div>

        {/* Assessment Summary */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-foreground">Risk & Complexity Assessment</h2>
            {hasAssessment && (
              <button
                onClick={() => navigate('/assess')}
                className="flex items-center gap-1 text-xs text-cot-blue hover:underline"
              >
                View full results
                <ChevronRight className="w-3 h-3" />
              </button>
            )}
          </div>

          {hasAssessment ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Card className="dashboard-card">
                <CardContent className="p-4 space-y-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Risk Level</p>
                  <Badge variant="outline" className={cn('text-sm font-semibold', levelBadgeClass(project.risk_level))}>
                    {project.risk_level}
                  </Badge>
                  {project.avg_risk != null && (
                    <p className="text-xs text-muted-foreground tabular-nums">{project.avg_risk} / 500</p>
                  )}
                </CardContent>
              </Card>

              <Card className="dashboard-card">
                <CardContent className="p-4 space-y-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Complexity</p>
                  <Badge variant="outline" className={cn('text-sm font-semibold', levelBadgeClass(project.complexity_level))}>
                    {project.complexity_level}
                  </Badge>
                  {project.avg_complexity != null && (
                    <p className="text-xs text-muted-foreground tabular-nums">{project.avg_complexity} / 500</p>
                  )}
                </CardContent>
              </Card>

              <Card className="dashboard-card">
                <CardContent className="p-4 space-y-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Category</p>
                  {project.category != null ? (
                    <>
                      <Badge variant="outline" className={cn('text-sm font-bold', categoryBadgeClass(project.category))}>
                        Cat {project.category}
                      </Badge>
                      <p className="text-xs text-muted-foreground">
                        {project.category === 4 ? 'Critical' :
                         project.category === 3 ? 'High' :
                         project.category === 2 ? 'Moderate' : 'Low'}
                      </p>
                    </>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </CardContent>
              </Card>

              <Card className="dashboard-card">
                <CardContent className="p-4 space-y-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Last Updated</p>
                  <p className="text-sm font-medium text-foreground">
                    {formatDate(project.updated_at)}
                  </p>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="rounded-lg border border-border bg-muted/20 px-6 py-6 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-foreground">No assessment on file</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Run the assessment wizard to score this project's risk and complexity.
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => navigate('/assess')}
                className="shrink-0 bg-cot-blue hover:bg-cot-blue/90 text-white"
              >
                Start Assessment
              </Button>
            </div>
          )}
        </div>

        <Separator />

        {/* Documents */}
        <div className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">Project Documents</h2>
          {id && <DocumentsSection projectId={id} />}
        </div>

      </div>
    </div>
  );
}
